import { ServiceException, ShoppingCartException } from '@inter-ikea-kompis/exceptions';
import type { ICart, IFailedShoppingItem, IShoppingItem } from '@inter-ikea-kompis/types';
import type { CacheStore } from '@inter-ikea-kompis/utilities';
import { LocaleUtility } from '@inter-ikea-kompis/utilities';

import BitBoxUtility from '../../../utilities/BitBoxUtility.js';
import IframeManager from '../../../utilities/IframeManager.js';
import ServiceFetch from '../../../utilities/ServiceFetch.js';
import NifUnitTypeEnum from './enums/NifUnitTypeEnum.js';
import type INifCartResponse from './types/INifCartResponse.js';

/**
 * This class is used for CRUD operations for NIF eCommerce API.
 */
export default class NifCartService {
	private locale: string;
	private endpoint: string;
	private frameContextEndpoint: string;
	private nifCartUseBearerToken: boolean;
	private readonly cache: CacheStore;

	private iframeManager: IframeManager | null;

	constructor(options: {
		locale: string;
		endpoint: string;
		frameContextEndpoint: string;
		nifCartUseBearerToken: boolean;
		cache: CacheStore;
	}) {
		this.locale = options.locale;
		this.endpoint = options.endpoint;
		this.frameContextEndpoint = options.frameContextEndpoint;
		this.nifCartUseBearerToken = options.nifCartUseBearerToken;
		this.cache = options.cache;
	}

	/**
	 * Adds items to shopping cart.
	 *
	 * @param options Options.
	 * @param options.items Array of shopping items.
	 */
	public async addToCart(options: { items: IShoppingItem[] }): Promise<ICart> {
		const carts = await this.getCarts();
		const cart = carts.length > 0 ? carts[0] : await this.createCart(NifUnitTypeEnum.retailUnit);

		try {
			await this.addCartItems({ items: options.items, cartId: cart.id });
		} catch (error) {
			const { serverResponse } = error;

			if (serverResponse?.status === 406) {
				const failedShoppingItems = await this.getFailedShoppingItems(serverResponse);

				if (failedShoppingItems.length > 0) {
					throw new ShoppingCartException(failedShoppingItems, []);
				}
			}
		}

		const cartCount = await this.getCartCountById(cart.id);

		return {
			errorList: [],
			cartCount
		};
	}

	/**
	 * Fetches and returns the cart count.
	 *
	 */
	public async getCartCount(): Promise<number> {
		const carts = await this.getCarts();

		if (carts.length > 0) {
			return this.getCartCountById(carts[0].id);
		}

		return 0;
	}

	/**
	 * Parses Error ServerResponse and returns array of failed shopping items.
	 *
	 * @param serverResponse ...
	 */
	private async getFailedShoppingItems(serverResponse): Promise<Array<IFailedShoppingItem>> {
		const response = <IFailedShoppingItem[]>await serverResponse.json();

		const items = Array<IFailedShoppingItem>();
		if (response) {
			for (const item of response) {
				if (item.itemNo && item.message) {
					items.push(item);
				}
			}
		}

		return items;
	}

	/**
	 * Fetches and returns the cart count.
	 *
	 * @param cartId Cart id.
	 */
	private async getCartCountById(cartId: string): Promise<number> {
		const response = await ServiceFetch.json(`${this.endpoint}/carts/${cartId}`, {
			method: 'GET',
			credentials: 'include',
			headers: {
				Accept: 'application/json',
				...(await this.getAuthorizationHeaders())
			}
		});
		return response.quantity || 0;
	}

	/**
	 * Fetches all carts and returns them. Returns an empty array if 0 (zero) carts.
	 */
	private async getCarts(): Promise<INifCartResponse[]> {
		return <INifCartResponse[]>await ServiceFetch.json(`${this.endpoint}/carts`, {
			method: 'GET',
			credentials: 'include',
			headers: {
				Accept: 'application/json',
				...(await this.getAuthorizationHeaders())
			}
		});
	}

	/**
	 * Creates a cart and returns the created cart.
	 *
	 * @param retailUnitType The type of unit (ru = Retail Unit/Market or sto = Store).
	 */
	private async createCart(retailUnitType: NifUnitTypeEnum): Promise<INifCartResponse> {
		const { country, language } = LocaleUtility.getCountryAndLanguage(this.locale);

		return <INifCartResponse>await ServiceFetch.json(`${this.endpoint}/carts`, {
			method: 'POST',
			credentials: 'include',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
				...(await this.getAuthorizationHeaders())
			},
			body: JSON.stringify({
				context: {
					id: country,
					type: retailUnitType
				},
				countryCode: country,
				languageCode: language
			})
		});
	}

	/**
	 * Add cart items to cart.
	 *
	 * @param options Configuration.
	 * @param options.items Array of shopping items.
	 * @param options.cartId Cart id.
	 */
	private async addCartItems(options: { items: IShoppingItem[]; cartId: string }): Promise<void> {
		await ServiceFetch.text(`${this.endpoint}/carts/${options.cartId}/items`, {
			method: 'POST',
			credentials: 'include',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
				...(await this.getAuthorizationHeaders())
			},
			body: JSON.stringify({
				items: options.items.map((item) => {
					return {
						itemNo: item.id,
						type: item.type,
						quantity: item.quantity
					};
				})
			})
		});
	}

	/**
	 * Retrieves authorization token from iframe and returns it as an
	 * authorization header. If third party domain token retrieval is not enabled
	 * this method will return an empty header object. This should cause the
	 * calling function to fall back to using cookies.
	 *
	 * We need to use an iframe to get context information because sometimes the
	 * application is hosted on a domain other than the one of the primary market
	 * site. When this is the case sometimes cookies can't be read cross-site
	 * because of privacy settings in some users browsers, especially in private
	 * browsing mode. This causes a cart to be created separate from the cart used
	 * on the primary site.
	 */
	private async getAuthorizationHeaders(): Promise<HeadersInit> {
		if (!this.nifCartUseBearerToken) return {};

		const token = await this.getToken();

		return {
			Authorization: `Bearer ${token}`
		};
	}

	/**
	 * Retrieves authorization token from iframe. The iframe instance will be
	 * cached so calling this multiple times will not create multiple iframes.
	 */
	private async getToken(): Promise<string> {
		const iframe = await this.createIframe();
		const context = await BitBoxUtility.readIframeContext(iframe);

		// Context is a union type of v1 and v2 context. TypeScript isn't happy if
		// we just check thuthiness.
		if (!('token' in context)) {
			throw new ServiceException('Token not returned from iframe.');
		}

		return context.token;
	}

	/**
	 * Creates and returns iframe element.
	 *
	 * @returns Promise with iframe.
	 */
	private async createIframe(): Promise<HTMLIFrameElement> {
		if (this.iframeManager && this.iframeManager.url !== this.frameContextEndpoint) {
			this.iframeManager.disconnect();
			this.iframeManager = null;
		}

		if (!this.iframeManager) {
			this.iframeManager = new IframeManager(this.frameContextEndpoint, this.cache);
		}

		return await this.iframeManager.getIframe();
	}
}
